// Исторические записи из старого журнала My Car.
//
// Это не «начальные данные»: INITIAL_* применяются только при самом первом
// запуске, а на устройстве, где localStorage уже заполнен, они игнорируются.
// Поэтому история оформлена как разовая миграция (см. HISTORY_IMPORTS.id):
// приложение один раз доливает недостающие записи в уже существующие данные
// и запоминает выполненный импорт в KEYS.imports.
//
// Правила источника:
//   • неизвестное значение — null, а не 0 и не догадка;
//   • ни одна запись не удаляется и не изменяется, добавляются только новые;
//   • признак дубля для заправки — совпадение пробега (приложение и так
//     запрещает две заправки с одинаковым km), для ТО — дата + вид работ.

import { migrateFuel, migrateReminders, migrateService, nextId, numOrNull } from "./utils";
import { daysBetween, isValidISODate, mileagePoints } from "./analytics";
import {
  completedBy, dueFromInterval, plannedReminderFields, serviceReminderNote,
  staleOilReminders,
} from "./serviceReminder";

/* ---------------- заправки ---------------- */
// station/note взяты из старого журнала как есть.
//
// Признак «полный бак» в старом журнале не сохранился. Модель требует boolean,
// поэтому берётся значение по умолчанию (полный), но расход по таким записям
// НЕ рассчитывается: без реальных границ цикла получались бы числа вроде
// 1,87 и 23,18 л/100 км, которые испортили бы средний расход. Как только
// у записи проставлен настоящий признак бака, обычное редактирование
// пересчитает расход штатным recalcFrom.

const FUEL_2025 = [
  { date:"2025-06-10", km:189000, liters:26.000, pricePerL:1.539, grossTotal:40.00, station:"Repsol" },
  { date:"2025-06-21", km:189700, liters:28.000, pricePerL:1.643, grossTotal:46.00, station:"Repsol" },
  { date:"2025-06-30", km:190300, liters:30.000, pricePerL:1.550, grossTotal:46.50, station:"Repsol Gijón" },
  { date:"2025-07-12", km:190900, liters:26.600, pricePerL:1.459, grossTotal:38.80, station:"Repsol" },
  { date:"2025-07-19", km:191536, liters:26.300, pricePerL:1.521, grossTotal:40.00, station:"Alcampo El Entrego" },
  { date:"2025-07-26", km:192000, liters:31.270, pricePerL:1.599, grossTotal:50.00, station:"Repsol, Oviedo" },
  { date:"2025-08-08", km:192827, liters:25.332, pricePerL:1.579, grossTotal:40.00, station:"Oviedo" },
  { date:"2025-08-15", km:193304, liters:29.630, pricePerL:1.350, grossTotal:40.00, station:"Хихон" },
  { date:"2025-08-23", km:193988, liters:23.818, pricePerL:1.679, grossTotal:39.99, station:"Gijón" },
  { date:"2025-08-30", km:194404, liters:24.860, pricePerL:1.609, grossTotal:40.00, station:"Oviedo" },
  { date:"2025-09-03", km:194808, liters:28.996, pricePerL:1.379, grossTotal:40.00, station:"El Entrego" },
  { date:"2025-09-06", km:195487, liters:25.642, pricePerL:1.565, grossTotal:40.13, station:"Madrid" },
  { date:"2025-09-07", km:195757, liters:23.631, pricePerL:1.589, grossTotal:37.55, station:"Burgos" },
  { date:"2025-09-13", km:196330, liters:29.007, pricePerL:1.379, grossTotal:40.00, station:"Alcampo" },
  { date:"2025-09-21", km:197085, liters:17.868, pricePerL:1.679, grossTotal:30.00, station:"" },
  { date:"2025-09-27", km:197451, liters:24.860, pricePerL:1.609, grossTotal:40.00, station:"Oviedo" },
  { date:"2025-10-08", km:197955, liters:37.611, pricePerL:1.649, grossTotal:62.02, station:"Хихон" },
  { date:"2025-10-19", km:198722, liters:15.209, pricePerL:1.699, grossTotal:25.84, station:"" },
  { date:"2025-10-26", km:199010, liters:11.320, pricePerL:1.659, grossTotal:18.78, station:"" },
  { date:"2025-11-01", km:199231, liters:23.972, pricePerL:1.669, grossTotal:40.01, station:"" },
  { date:"2025-11-08", km:199631, liters:31.301, pricePerL:1.599, grossTotal:50.05, station:"" },
  { date:"2025-11-08", km:200059, liters:25.199, pricePerL:null,  grossTotal:39.79, station:"Madrid",
    note:"Цена за литр в старом журнале перекрыта — не восстановлена" },
  { date:"2025-11-13", km:200776, liters:25.960, pricePerL:1.619, grossTotal:42.03, station:"Benidorm" },
  { date:"2025-11-19", km:201208, liters:30.952, pricePerL:1.629, grossTotal:50.42, station:"Benidorm" },
  { date:"2025-11-26", km:201672, liters:20.981, pricePerL:1.559, grossTotal:32.71, station:"Трасса, Madrid" },
  { date:"2025-11-26", km:202137, liters:25.823, pricePerL:null,  grossTotal:40.00, station:"",
    note:"Цена за литр в старом журнале перекрыта — не восстановлена" },
  { date:"2025-12-05", km:202641, liters:25.243, pricePerL:1.585, grossTotal:40.01, station:"Gijón" },
  { date:"2025-12-09", km:203024, liters:25.570, pricePerL:1.569, grossTotal:40.12, station:"Ribadesella" },
  { date:"2025-12-27", km:203631, liters:26.230, pricePerL:1.525, grossTotal:40.00, station:"Oviedo" },
];

const FUEL_2026 = [
  { date:"2026-01-05", km:204058, liters:26.333, pricePerL:1.519, grossTotal:40.00, station:"Oviedo" },
  { date:"2026-01-10", km:204444, liters:28.450, pricePerL:1.529, grossTotal:43.50, station:"Bilbao" },
  { date:"2026-01-11", km:205121, liters:12.660, pricePerL:1.579, grossTotal:19.99, station:"Caparoca, Сарагоса" },
  { date:"2026-01-11", km:205251, liters:30.137, pricePerL:1.535, grossTotal:46.26, station:"Caparoca, Сарагоса" },
  { date:"2026-01-16", km:205954, liters:null,   pricePerL:null,  grossTotal:null,  station:"Трасса, Хихон",
    note:"Объём, цена и сумма в старом журнале перекрыты — не восстановлены" },
  { date:"2026-01-22", km:206421, liters:25.110, pricePerL:1.593, grossTotal:40.00, station:"" },
  { date:"2026-01-31", km:206919, liters:25.110, pricePerL:1.593, grossTotal:40.00, station:"" },
  { date:"2026-02-06", km:207389, liters:24.882, pricePerL:1.608, grossTotal:40.01, station:"" },
  { date:"2026-02-14", km:207908, liters:28.037, pricePerL:1.605, grossTotal:45.00, station:"Хихон" },
  { date:"2026-02-21", km:208369, liters:24.272, pricePerL:1.648, grossTotal:40.00, station:"Siero" },
  { date:"2026-03-01", km:208952, liters:24.178, pricePerL:1.654, grossTotal:39.99, station:"" },
  { date:"2026-03-06", km:209368, liters:24.257, pricePerL:1.649, grossTotal:40.00, station:"Хихон" },
];

/* ---------------- ТО, ремонты и расходы ---------------- */
// Только записи, которых в журнале ещё нет. События, уже записанные одной
// объединённой строкой (08.08.2025, 10.09.2025, 19.02.2021), сюда не входят —
// они бы удвоили суммы; расхождения вынесены в отчёт.

const SERVICE = [
  { date:"2019-11-21", km:106500, type:"Покупка автомобиля", cost:null, note:"Benidorm", category:"other" },
  { date:"2019-11-27", km:106700, type:"Аккумулятор 12V", cost:59, note:"Benidorm", category:"battery" },
  { date:"2024-01-18", km:155347, type:"ITV Техосмотр", cost:null, note:"Benidorm", category:"inspection" },
  { date:"2024-11-21", km:170000, type:"Щётки стеклоочистителя", cost:20, note:"Asturias. Передние дворники 70 см и 36 см", category:"parts" },
  { date:"2024-11-21", km:null, type:"Страхование", cost:271.17, note:"Агент Татьяна, начало 21.11.2024", category:"insurance" },
  { date:"2025-02-21", km:178100, type:"Задний дворник", cost:15, note:"Asturias", category:"parts" },
  { date:"2025-05-24", km:183000, type:"Накачал шины", cost:null, note:"El Entrego. Стоимость неизвестна", category:"tires" },
  { date:"2025-08-08", km:null, type:"Масло в коробку автомат", cost:68, note:"Toyota магазин. Покупка", category:"parts" },
  { date:"2025-08-08", km:null, type:"Масло в коробку и фильтр", cost:84, note:"Toyota магазин. Покупка", category:"parts" },
  { date:"2025-08-13", km:null, type:"Свечи", cost:103, note:"Toyota магазин. Запчасти", category:"parts" },
];

/* ---------------- разбор расхождений со старым журналом ---------------- */
// Правки существующих записей: старый журнал точнее там, где в текущей базе
// стоял 0 (= «бесплатно») вместо неизвестной или реальной суммы, а текущая
// база точнее в пробегах и в разбивке уже уточнённых сумм.

const FUEL_PATCHES = [
  // дата заправки: в журнале 26.05, в базе стояло 23.05
  { match: { km: 215239 }, set: { date: "2026-05-26" } },
];

const SERVICE_PATCHES = [
  {
    // большой сервис остаётся одной записью на 150 € — четыре отдельные строки
    // задвоили бы расходы; детализация уходит в заметку
    match: { date: "2025-08-08", type: "Стойка стаб. + прокладка натяжителя + тормозная жидкость DOT4 + балансировка" },
    set: {
      note:
        "Олександр, Овьедо. Стойка стабилизатора 27 € + замена 37 €; " +
        "прокладка натяжителя цепи 5,30 € + замена 37 €; " +
        "тормозная жидкость DOT4 1 л 14 € + замена 15 €; " +
        "балансировка двух передних колёс 15 €. Ревизия ходовой — всё нормально.",
    },
  },
  {
    // 205 € — полная стоимость события; 100 € из старого журнала это только работа
    match: { date: "2025-09-10", type: "Масло 5W-30 + фильтр + жидкость вариатора + свечи" },
    set: {
      note:
        "Олександр. Свечи Toyota 105 € + работа 100 €. " +
        "Работа: свечи 30 €, масло коробки 30 €, моторное масло 40 €.",
    },
  },
  {
    // 0 € означало бы бесплатно, а стоимость на самом деле неизвестна
    match: { date: "2025-01-24", type: "ITV Техосмотр" },
    set: { cost: null, note: "Пройден. Перегорела лампа фары" },
  },
  {
    match: { date: "2025-02-14", type: "Масло и фильтр" },
    set: {
      cost: 100,
      note: "Алекс, Овьедо. Работа по замене 45 €; масло и фильтр куплены отдельно, около 56 €",
    },
  },
  {
    match: { date: "2025-04-01", type: "Ремонт суппорта левого тормоза" },
    set: { cost: 60, note: "Aleks Motors Oviedo. Греется диск, колодки полностью не отходят" },
  },
  {
    match: { date: "2024-06-08", type: "Масло и фильтр 0W-20" },
    set: { cost: 50, note: "lubricantesweb.es. Менял самостоятельно" },
  },
  {
    // пробег из старого журнала, стоимость остаётся более точной
    match: { date: "2021-11-13", type: "Задние тормозные колодки TRW" },
    set: { km: 126000, note: "TRW, куплены самостоятельно. AutoDoc" },
  },
];

/* ---------------- заправки 2024 по финансовому журналу ---------------- */
// В старом журнале MyCar заправок за 2024 год нет вовсе; в финансовом журнале
// видны только дата и списанная сумма. Пробег, литры и цена за литр
// неизвестны и остаются null (пробег в модели хранится как 0 = «неизвестен»,
// как у записей ТО без одометра). Такие записи участвуют только в деньгах —
// «Всего расходов», «Ежемесячные затраты», среднее €/мес — а в расход
// л/100 км и среднюю цену €/л не попадают: без литров и пробега их не посчитать.
// Сумма партии считается тестом из самого списка — 862,00 €.

const FIN_2024_NOTE = "Восстановлено по финансовому журналу: пробег, литры и цена неизвестны";

const FUEL_2024 = [
  { date:"2024-05-04", km:null, paidTotal:142.00, note: FIN_2024_NOTE },
  { date:"2024-06-04", km:null, paidTotal:120.00, note: FIN_2024_NOTE },
  { date:"2024-07-04", km:null, paidTotal:70.00,  note: FIN_2024_NOTE },
  { date:"2024-08-26", km:null, paidTotal:120.00, note: FIN_2024_NOTE },
  { date:"2024-09-06", km:null, paidTotal:120.00, note: FIN_2024_NOTE },
  { date:"2024-10-01", km:null, paidTotal:120.00, note: FIN_2024_NOTE },
  { date:"2024-11-21", km:null, paidTotal:120.00, note: FIN_2024_NOTE },
  { date:"2024-12-01", km:null, paidTotal:50.00,  note: FIN_2024_NOTE },
];

// Остальные четыре операции 2024 года из журнала в базе уже есть:
//   25.09 шины 254,00 € и 21.11 страховка 271,17 € — совпадают точь-в-точь;
//   04.06 масло 62,79 € — в базе замена 08.06 на 50 € (lubricantesweb.es):
//     похоже, тот же заказ, но дата и сумма расходятся — не трогается;
//   21.11 дворники 17,71 € — в базе те же дворники на 20 € — не трогается.

/* ---------------- восстановление пробега по опорным точкам ---------------- */
// Записям без одометра (заправки 2024 по финансовому журналу, покупки
// запчастей, страховка) пробег восстанавливается линейной интерполяцией по
// датам между ближайшими реальными показаниями: подтверждённые точки из
// истории автомобиля ниже плюс все реальные значения km, уже записанные в
// базе (через mileagePoints — он же отбрасывает откаты одометра назад).
// Результат округляется до 10 км и не выходит за соседние точки. Запись
// с уже известным пробегом не трогается; запись до первой или после
// последней точки остаётся без пробега — среднее по автомобилю не применяется.
// Восстановленный пробег — рабочее историческое значение для журнала и
// графика пробега; расход и цену за литр он не даёт: литров у записи нет.

export const ODOMETER_ANCHORS = [
  { date:"2016-05-23", km:80472 },
  { date:"2018-02-19", km:91714 },
  { date:"2018-05-18", km:93427 },
  { date:"2019-05-06", km:101803 },
  { date:"2019-11-21", km:106500 },
  { date:"2019-11-27", km:106700 },
  { date:"2019-11-30", km:107000 },
  { date:"2019-12-13", km:107600 },
  { date:"2020-02-12", km:110100 },
  { date:"2020-02-13", km:110105 },
  { date:"2020-05-29", km:111655 },
  { date:"2020-07-09", km:112251 },
  { date:"2020-08-11", km:113375 },
  { date:"2020-10-28", km:115500 },
  { date:"2020-10-29", km:115500 },
  { date:"2021-02-19", km:118856 },
  { date:"2021-08-07", km:124000 },
  { date:"2022-01-14", km:128277 },
  { date:"2022-05-20", km:132000 },
  { date:"2022-09-01", km:135790 },
  { date:"2023-09-21", km:151500 },
  { date:"2024-01-18", km:155347 },
  { date:"2024-01-25", km:155870 },
  { date:"2024-04-05", km:160000 },
  { date:"2024-06-08", km:164700 },
  { date:"2024-09-25", km:174000 },
  { date:"2025-01-24", km:176540 },
  { date:"2025-02-14", km:178000 },
  { date:"2025-03-25", km:180000 },
  { date:"2025-04-01", km:182029 },
  { date:"2025-08-08", km:192820 },
  { date:"2025-09-10", km:196113 },
  { date:"2025-09-18", km:196845 },
  { date:"2026-02-19", km:208188 },
];

/**
 * Опорные точки одометра: подтверждённые из истории автомобиля плюс реальные
 * показания из базы. Отсортированы по дате (внутри дня — по пробегу),
 * без дублей, монотонны: точка ниже уже достигнутого пробега отбрасывается.
 */
export function odometerAnchors(fuel = [], service = [], confirmed = ODOMETER_ANCHORS) {
  const raw = [
    ...confirmed,
    ...mileagePoints(fuel, service).map((p) => ({ date: p.date, km: p.km })),
  ].sort((a, b) => a.date.localeCompare(b.date) || a.km - b.km);
  const out = [];
  let max = -Infinity;
  raw.forEach((p) => {
    if (p.km < max) return;
    const last = out[out.length - 1];
    if (last && last.date === p.date && last.km === p.km) return;
    max = p.km;
    out.push(p);
  });
  return out;
}

/**
 * Пробег на дату между ближайшими опорными точками:
 *   km = kmBefore + (kmAfter − kmBefore) × прошло дней / всего дней,
 * округлённый до 10 км и зажатый между соседними точками. Точка в тот же
 * день — её пробег (при нескольких — наименьший). Вне диапазона точек — null.
 */
export function interpolateKm(anchors, date) {
  const sameDay = anchors.find((a) => a.date === date);
  if (sameDay) return sameDay.km;
  let before = null;
  let after = null;
  for (const a of anchors) {
    if (a.date < date) before = a;
    else if (!after) after = a;
  }
  if (!before || !after) return null;
  const total = daysBetween(before.date, after.date);
  const elapsed = daysBetween(before.date, date);
  const raw = before.km + ((after.km - before.km) * elapsed) / total;
  const rounded = Math.round(raw / 10) * 10;
  return Math.min(after.km, Math.max(before.km, rounded));
}

/** Заполняет пробег только у записей, где он неизвестен (null / 0). */
export function fillMissingKm(list, anchors) {
  return list.map((e) => {
    if (!e || !isValidISODate(e.date)) return e;
    const known = numOrNull(e.km);
    if (known !== null && known > 0) return e;
    const km = interpolateKm(anchors, e.date);
    return km === null ? e : { ...e, km };
  });
}

/* ---------------- сверка с финансовым журналом: осень 2025 ---------------- */
// Старый журнал MyCar хранил сумму по чеку (gross), а в финансовом журнале
// видно реально списанное — с учётом скидок на АЗС. Это те же заправки
// (даты совпадают до дня), поэтому новых записей нет: у существующих
// уточняется paidTotal, чек остаётся в grossTotal, разница уходит в discount.
// Записи 13.06 (132,00 €) и 01.07 (128,50 €) из журнала — итоги месяца по
// категории (в базе июнь 132,50 € и июль 128,80 € тремя заправками каждый),
// как заправки они не добавляются. 08.08 (117,23 € против 40,00 €) — расхождение
// не объяснено, запись не трогается.
// `when` — защита от затирания: правка применяется, только если сумма в записи
// всё ещё старая; отредактированная пользователем запись остаётся как есть.

const FUEL_PATCHES_FIN_2025 = [
  { match: { km: 195487 }, when: { paidTotal: 40.13 }, set: { paidTotal: 40.00, discount: 0.13 } },
  // списано больше, чем стояло в чеке: 37,55 было ошибкой старого журнала, чек 38,00
  { match: { km: 195757 }, when: { paidTotal: 37.55 }, set: { grossTotal: 38.00, paidTotal: 38.00, discount: 0 } },
  { match: { km: 197451 }, when: { paidTotal: 40.00 }, set: { paidTotal: 37.57, discount: 2.43 } },
  { match: { km: 197955 }, when: { paidTotal: 62.02 }, set: { paidTotal: 60.00, discount: 2.02 } },
  { match: { km: 198722 }, when: { paidTotal: 25.84 }, set: { paidTotal: 21.63, discount: 4.21 } },
  { match: { km: 199010 }, when: { paidTotal: 18.78 }, set: { paidTotal: 17.26, discount: 1.52 } },
];

const SERVICE_SPLITS = [
  {
    // две работы с разными интервалами следующей замены; сумма 215 € сохраняется
    match: { date: "2021-02-19", type: "Замена масла вариатора + антифриз (2 контура)" },
    into: [
      { type: "Замена масла вариатора", cost: 100, note: "Toyota Центр Бенидорм", category: "fluid" },
      { type: "Антифриз (2 контура)", cost: 115, note: "Toyota Центр Бенидорм", category: "fluid" },
    ],
  },
];

/**
 * Разделение записей, где под одной карточкой было объединено несколько
 * самостоятельных работ. Каждая работа получает свою карточку, категорию
 * и (где известна) свою стоимость — детализация взята из заметки прежней
 * объединённой записи, ничего не досчитано и не придумано.
 */
const SERVICE_SPLITS_2 = [
  {
    // сумма 4 позиций 150,30 € на 0,30 € больше фактически оплаченных 150 € —
    // расхождение сохранено как есть, каждая карточка поясняет расчёт
    match: { date: "2025-08-08", type: "Стойка стаб. + прокладка натяжителя + тормозная жидкость DOT4 + балансировка" },
    into: [
      {
        type: "Замена стойки стабилизатора", cost: 64, category: "suspension",
        note:
          "Олександр, Овьедо. Стойка стабилизатора 27 € + работа 37 €. Ревизия ходовой при визите — всё в норме. " +
          "Разделено из общей записи 08.08.2025: сумма по 4 позициям 150,30 €, фактически оплачено за визит 150 €.",
      },
      {
        type: "Замена прокладки натяжителя цепи", cost: 42.30, category: "other",
        note:
          "Олександр, Овьедо. Прокладка натяжителя цепи 5,30 € + работа 37 €. " +
          "Часть визита 08.08.2025 (сумма по 4 позициям 150,30 €, фактически оплачено 150 €).",
      },
      {
        type: "Замена тормозной жидкости DOT4", cost: 29, category: "fluid",
        note:
          "Олександр, Овьедо. Тормозная жидкость DOT4 1 л 14 € + работа 15 €. " +
          "Часть визита 08.08.2025 (сумма по 4 позициям 150,30 €, фактически оплачено 150 €).",
      },
      {
        type: "Балансировка передних колёс", cost: 15, category: "tires",
        note:
          "Олександр, Овьедо. Балансировка двух передних колёс. " +
          "Часть визита 08.08.2025 (сумма по 4 позициям 150,30 €, фактически оплачено 150 €).",
      },
    ],
  },
  {
    // сумма 3 позиций 205 € совпадает с исходной стоимостью визита без остатка
    match: { date: "2025-09-10", type: "Масло 5W-30 + фильтр + жидкость вариатора + свечи" },
    into: [
      {
        type: "Замена моторного масла 5W-30 и фильтра", cost: 40, category: "oil",
        note: "Олександр. Работа по замене масла и фильтра 40 €. Стоимость масла и фильтра в сумму не входит — куплены отдельно.",
      },
      {
        type: "Замена жидкости вариатора", cost: 30, category: "fluid",
        note: "Олександр. Работа по замене жидкости вариатора 30 €. Стоимость жидкости в сумму не входит — куплена отдельно.",
      },
      {
        type: "Замена свечей зажигания", cost: 135, category: "other",
        note:
          "Олександр. Свечи Toyota 105 € (материал) + работа 30 €. " +
          "Возможно совпадает с покупкой «Свечи» от 13.08.2025 (103 €) — требует проверки, пока не объединено.",
      },
    ],
  },
];

/**
 * Исправление двойного учёта свечей: карточка «Замена свечей зажигания»
 * (родилась в mycar-splits-2026-09-2) включала материал 105 € — приблизительную
 * цифру из исходной заметки визита. Но материал уже учтён отдельной записью
 * «Свечи» от 13.08.2025 за 103 € (точная сумма чека). Стоимость карточки
 * снижается до 30 € — это только работа по установке, деталь не задваивается.
 * Прежняя миграция (уже выпущенная в прод) не редактируется задним числом —
 * это отдельный корректирующий пакет, применяется поверх неё.
 */
const SERVICE_PATCHES_2 = [
  {
    match: { date: "2025-09-10", type: "Замена свечей зажигания" },
    set: {
      cost: 30,
      note:
        "Олександр, Овьедо. Работа 30 €. Свечи Toyota куплены ранее 13.08.2025 за 103 € " +
        "и находятся отдельной записью в истории покупок.",
    },
  },
];

/* ---------------- замена масла 14.09.2026 ---------------- */
// Первая запись с раздельными расходами: работа мастера отдельно от масла
// и фильтра, общая стоимость — сумма позиций (масло и фильтр в чеке одной
// строкой, поэтому они в одной позиции). Задача на следующую замену
// создаётся так же, как её создала бы форма с включённым планом, а прежняя
// задача «Моторное масло 0W-20» закрывается этой записью.

const OIL_CHANGE_2026_09 = {
  date: "2026-09-14", km: 221570,
  type: "Масло Toyota 0W-20 + фильтр MANN HU 6006 z",
  cost: 92.88,
  costs: { labor: 30, oil: 62.88, filter: null, other: null },
  note: "Ростик Felguera",
  category: "oil",
};

const OIL_SOURCE_2026_09 = { date: OIL_CHANGE_2026_09.date, type: OIL_CHANGE_2026_09.type };

const REMINDER_COMPLETIONS_2026_09 = [
  { match: { title: "Моторное масло 0W-20" }, by: OIL_SOURCE_2026_09 },
];

const PLANNED_REMINDERS_2026_09 = [
  { source: OIL_SOURCE_2026_09, intervalKm: 13000, intervalMonths: 12, oilGrade: "0W-20" },
];

/**
 * Корректировка интервала: первая версия партии выпущена с 15 000 км, а для
 * этой замены нужен интервал 13 000 км (234 570 км / 14.09.2027). На устройстве,
 * где партия уже создала задачу на 236 570 км, пересчитывается именно она —
 * активная задача, созданная из этой записи и всё ещё с прежним интервалом.
 * Задачу, интервал которой пользователь уже менял сам, миграция не трогает;
 * там, где первая партия ещё не применялась, она сразу создаёт 234 570 км,
 * и корректировке нечего делать.
 */
const PLANNED_REMINDER_PATCHES_2026_09 = [
  {
    source: OIL_SOURCE_2026_09,
    from: { intervalKm: 15000, intervalMonths: 12 },
    to: { intervalKm: 13000, intervalMonths: 12 },
  },
];

/**
 * Закрытие старой задачи о масле там, где партия oil-change-2026-09-14 её
 * не нашла. Та партия закрывала задачу по дословному названию «Моторное
 * масло 0W-20» и применялась один раз: на устройстве, где задачу успели
 * поправить вручную (221 600 км / 14.09.2026 вместо исходных
 * 221 000 / 15.09.2026 — и, судя по всему, название тоже набрано заново)
 * или вернуть в работу, совпадения не было, партия отметилась выполненной,
 * и задача так и осталась активной и просроченной рядом с новой на
 * 234 570 км. Здесь закрываются все устаревшие задачи о моторном масле
 * по правилу формы ТО (staleOilReminders): активные, не созданные из этой
 * замены и со сроком не позже следующей запланированной. Запись ТО и
 * задача следующей замены не меняются, новых задач не создаётся.
 */
const REMINDER_COMPLETIONS_2026_09_STALE = [
  { match: { staleOil: true }, by: OIL_SOURCE_2026_09 },
];

/** Разовые миграции истории. Каждая применяется не более одного раза. */
export const HISTORY_IMPORTS = [
  {
    id: "mycar-history-2019-2026",
    fuel: [...FUEL_2025, ...FUEL_2026],
    service: SERVICE,
  },
  {
    id: "mycar-conflicts-2026-09",
    fuelPatches: FUEL_PATCHES,
    servicePatches: SERVICE_PATCHES,
    serviceSplits: SERVICE_SPLITS,
  },
  {
    id: "mycar-splits-2026-09-2",
    serviceSplits: SERVICE_SPLITS_2,
  },
  {
    id: "mycar-svechi-fix-2026-09-3",
    servicePatches: SERVICE_PATCHES_2,
  },
  {
    id: "oil-change-2026-09-14",
    service: [OIL_CHANGE_2026_09],
    reminderCompletions: REMINDER_COMPLETIONS_2026_09,
    plannedReminders: PLANNED_REMINDERS_2026_09,
  },
  {
    id: "oil-change-2026-09-14-interval-13000",
    plannedReminderPatches: PLANNED_REMINDER_PATCHES_2026_09,
  },
  {
    id: "oil-change-2026-09-14-close-stale",
    reminderCompletions: REMINDER_COMPLETIONS_2026_09_STALE,
  },
  {
    id: "fuel-fin-journal-2025-09-10",
    fuelPatches: FUEL_PATCHES_FIN_2025,
  },
  {
    id: "fuel-fin-journal-2024",
    fuel: FUEL_2024,
  },
  {
    id: "odometer-fill-2024",
    kmFill: true,
  },
  {
    // дворники 21.11.2024 стояли на 170 000 км — откат после шин 25.09.2024
    // (174 000). Пробег той же даты у страховки и заправки — 175 200
    // (интерполяция 25.09 → 24.01.2025); ставится тот же. 21.05.2026
    // (214 500 против заправки 14.05 на 214 503) — округление, не трогается.
    id: "wipers-2024-11-21-km",
    servicePatches: [
      { match: { date: "2024-11-21", type: "Щётки стеклоочистителя" }, when: { km: 170000 }, set: { km: 175200 } },
    ],
  },
  {
    // Заправка 04.07.2026 / 217 151 км несла total:41.31 и consumption:null
    // с самого первого коммита приложения (509ffb5) — до объединения с
    // журналом MyCar эта запись миграциями не трогалась вовсе. Скриншот
    // исходной записи в MyCar показывает сумму 42.95 € (= 25.490 л × 1.685 €/л,
    // без скидки — совпадает с точностью до цента) и расход 4.9 л/100 км.
    // Оба значения берутся из источника как есть, а не пересчитываются:
    // цикл этой заправки не совпадает с «от предыдущего полного бака» —
    // производная от соседних пробегов дала бы другое, ложное число.
    id: "fuel-2026-07-04-source-fix",
    fuelPatches: [
      {
        match: { km: 217151 },
        when: { grossTotal: 41.31, paidTotal: 41.31, consumption: null },
        set: { grossTotal: 42.95, paidTotal: 42.95, consumption: 4.9 },
      },
    ],
  },
];

/* ---------------- слияние без дублей ---------------- */

const FUEL_DEFAULTS = {
  liters: null, pricePerL: null, grossTotal: null, discount: 0,
  paidTotal: null, station: "", fullTank: true, note: "",
};

const hasKm = (e) => numOrNull(e.km) !== null && Number(e.km) > 0;
const fuelMoneyKey = (e) => `${e.date}|${numOrNull(e.paidTotal)}`;

/**
 * Добавляет заправки, которых ещё нет. Дубль определяется по пробегу:
 * приложение само запрещает две заправки с одинаковым km, поэтому запись
 * с уже занятым пробегом считается той же самой, даже если дата отличается.
 * Запись без пробега (восстановленная по финансовому журналу) сверяется
 * по дате и оплаченной сумме — как с существующими, так и внутри партии.
 * Расход импортированных записей остаётся пустым, а сохранённые значения
 * существующих записей не пересчитываются — статистика не меняется.
 */
export function mergeFuel(existing, incoming) {
  const takenKm = new Set(existing.filter(hasKm).map((e) => e.km));
  const takenMoney = new Set(existing.map(fuelMoneyKey));
  const added = [];
  const skipped = [];
  let id = nextId(existing);

  incoming.forEach((r) => {
    const dup = hasKm(r) ? takenKm.has(r.km) : takenMoney.has(fuelMoneyKey(r));
    if (dup) {
      skipped.push(r);
      return;
    }
    if (hasKm(r)) takenKm.add(r.km);
    takenMoney.add(fuelMoneyKey(r));
    added.push({ ...FUEL_DEFAULTS, ...r, id: id++, consumption: null });
  });

  if (!added.length) return { list: existing, added, skipped };
  return { list: migrateFuel([...existing, ...added]), added, skipped };
}

const serviceKey = (s) =>
  `${s.date}|${String(s.type || "").toLowerCase().trim()}`;

/** Добавляет записи ТО, которых ещё нет. Ключ дубля — дата + вид работ. */
export function mergeService(existing, incoming) {
  const taken = new Set(existing.map(serviceKey));
  const added = [];
  const skipped = [];
  let id = nextId(existing);

  incoming.forEach((r) => {
    if (taken.has(serviceKey(r))) {
      skipped.push(r);
      return;
    }
    taken.add(serviceKey(r));
    added.push({ note: "", category: "other", ...r, id: id++ });
  });

  if (!added.length) return { list: existing, added, skipped };
  return { list: migrateService([...existing, ...added]), added, skipped };
}

/* ---------------- применение партии миграции ---------------- */

const matchesService = (s, m) =>
  s.date === m.date &&
  String(s.type || "").toLowerCase().trim() === String(m.type || "").toLowerCase().trim();

/**
 * Заправки партии: сначала новые записи, затем точечные правки существующих,
 * затем восстановление пробега (kmFill) по опорным точкам — для них нужны и
 * записи ТО, поэтому они передаются третьим аргументом.
 * Правка с `when` применяется только к записи, у которой перечисленные поля
 * всё ещё равны ожидаемым: если пользователь уже поправил запись сам,
 * миграция её не трогает.
 */
export function applyFuelBatch(list, batch, service = []) {
  let out = batch.fuel && batch.fuel.length ? mergeFuel(list, batch.fuel).list : list;

  (batch.fuelPatches || []).forEach((p) => {
    const untouched = (f) =>
      !p.when || Object.entries(p.when).every(([k, v]) => f[k] === v);
    out = out.map((f) => (f.km === p.match.km && untouched(f) ? { ...f, ...p.set } : f));
  });

  if (batch.kmFill) {
    out = fillMissingKm(out, odometerAnchors(out, Array.isArray(service) ? service : []));
  }

  // правки не трогают литры и не меняют известный пробег, расход пересчитывать не нужно
  return out;
}

/**
 * Записи ТО партии: новые записи, точечные правки и разделение объединённых
 * событий. Разделение именно заменяет исходную запись, а не добавляется
 * поверх неё, — иначе сумма события удвоилась бы.
 */
export function applyServiceBatch(list, batch, fuel = []) {
  let out = batch.service && batch.service.length ? mergeService(list, batch.service).list : list;

  if (batch.kmFill) {
    out = fillMissingKm(out, odometerAnchors(Array.isArray(fuel) ? fuel : [], out));
  }

  // `when` — как у заправок: правка только для записи с ещё не изменёнными полями
  (batch.servicePatches || []).forEach((p) => {
    const untouched = (s) =>
      !p.when || Object.entries(p.when).every(([k, v]) => s[k] === v);
    out = out.map((s) => (matchesService(s, p.match) && untouched(s) ? { ...s, ...p.set } : s));
  });

  (batch.serviceSplits || []).forEach((split) => {
    const target = out.find((s) => matchesService(s, split.match));
    if (!target) return;
    let id = nextId(out);
    const parts = split.into.map((part, i) => ({
      ...target,
      ...part,
      id: i === 0 ? target.id : id++,
    }));
    out = out.flatMap((s) => (s.id === target.id ? parts : [s]));
  });

  return migrateService(out);
}

/**
 * Задачи партии. Ссылаются на записи ТО по дате и виду работ, поэтому
 * применяются к уже долитому списку service.
 *   reminderCompletions — какие активные задачи закрывает запись ТО
 *     (уже выполненные не трогаются): match.title — по дословному названию,
 *     match.staleOil — все устаревшие задачи о моторном масле по правилу
 *     формы ТО (см. staleOilReminders), кроме созданной из самой записи;
 *   plannedReminders — задачи на следующую замену, посчитанные от записи
 *     интервалом, как это делает форма ТО. Если из записи задача уже создана
 *     (формой или прежним импортом), новая не добавляется;
 *   plannedReminderPatches — смена интервала уже созданной задачи: только
 *     активной, созданной из указанной записи и с интервалом from — иначе
 *     задача считается изменённой пользователем и остаётся как есть.
 */
export function applyReminderBatch(list, service, batch) {
  let out = list;

  (batch.reminderCompletions || []).forEach((c) => {
    const entry = service.find((s) => matchesService(s, c.by));
    if (!entry) return;
    const stale = c.match.staleOil
      ? staleOilReminders(
          out, service, entry,
          out.find((r) => !r.completed && r.sourceServiceId === entry.id) || null
        )
      : out.filter((r) => !r.completed && r.title === c.match.title);
    if (!stale.length) return;
    out = out.map((r) => (stale.includes(r) ? { ...r, ...completedBy(entry) } : r));
  });

  (batch.plannedReminders || []).forEach((p) => {
    const entry = service.find((s) => matchesService(s, p.source));
    if (!entry || out.some((r) => r.sourceServiceId === entry.id)) return;
    const fields = plannedReminderFields(entry, {
      ...dueFromInterval(entry, p.intervalKm, p.intervalMonths),
      intervalKm: p.intervalKm,
      intervalMonths: p.intervalMonths,
      oilGrade: p.oilGrade,
    });
    out = [
      ...out,
      {
        id: nextId(out),
        priority: "upcoming",
        note: serviceReminderNote(entry, p.intervalKm, p.intervalMonths),
        completed: false,
        completedDate: null,
        completedKm: null,
        completedServiceId: null,
        ...fields,
      },
    ];
  });

  (batch.plannedReminderPatches || []).forEach((p) => {
    const entry = service.find((s) => matchesService(s, p.source));
    if (!entry) return;
    const isStale = (r) =>
      !r.completed &&
      r.sourceServiceId === entry.id &&
      r.intervalKm === p.from.intervalKm &&
      r.intervalMonths === p.from.intervalMonths;
    if (!out.some(isStale)) return;
    out = out.map((r) => {
      if (!isStale(r)) return r;
      const fields = plannedReminderFields(entry, {
        ...dueFromInterval(entry, p.to.intervalKm, p.to.intervalMonths),
        intervalKm: p.to.intervalKm,
        intervalMonths: p.to.intervalMonths,
        oilGrade: p.oilGrade,
      });
      // примечание обновляется, только если оно всё ещё автоматическое
      const autoNote =
        !r.note || r.note === serviceReminderNote(entry, p.from.intervalKm, p.from.intervalMonths);
      return {
        ...r,
        ...fields,
        // название и иконку задачи миграция не переименовывает
        title: r.title,
        icon: r.icon,
        note: autoNote ? serviceReminderNote(entry, p.to.intervalKm, p.to.intervalMonths) : r.note,
      };
    });
  });

  return out === list ? list : migrateReminders(out);
}
